<template>
  <div class="scms-tracker">
    <!-- Header Section -->
    <header class="dashboard-header">
      <div class="header-content">
        <h1>SCMS Tracker</h1>
        <p>SCMS visit analytics</p>
      </div>
    </header>

    <!-- Date Range Filter -->
    <div class="filter-section">
      <div class="filter-card">
        <h3>Date Range Filter</h3>
        <div v-if="loading" class="loading-indicator">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>Loading SCMS data...</span>
        </div>
        
        <div class="date-inputs">
          <div class="date-input-group">
            <label for="start-date">Start Date</label>
            <input
              id="start-date"
              v-model="startDate"
              type="date"
              class="date-input"
              :max="endDate"
            />
          </div>
          <div class="date-input-group">
            <label for="end-date">End Date</label>
            <input
              id="end-date"
              v-model="endDate"
              type="date"
              class="date-input"
              :min="startDate"
              :max="today"
            />
          </div>
          <button 
            @click="fetchSCMSData" 
            class="fetch-btn"
            :disabled="loading"
          >
            <i class="fa-solid fa-search" v-if="!loading"></i>
            <i class="fa-solid fa-spinner fa-spin" v-if="loading"></i>
            {{ loading ? 'Loading...' : 'Fetch Data' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-section">
      <div class="loading-card">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Loading SCMS data...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="error-section">
      <div class="error-card">
        <i class="fa-solid fa-exclamation-triangle"></i>
        <span>{{ error }}</span>
      </div>
    </div>

    <div v-if="analytics && !loading && !error" class="analytics-panel">
      <h3 class="analytics-panel-title">Visit analytics</h3>
      <p class="analytics-range">
        Range: {{ formatDate(analytics.start_date) }} – {{ formatDate(analytics.end_date) }}
      </p>
      <div class="analytics-grid">
        <div v-for="card in analyticsCards" :key="card.key" class="analytics-card">
          <span class="analytics-card-label">{{ card.label }}</span>
          <span class="analytics-card-value">{{ formatMetric(analytics[card.key]) }}</span>
        </div>
      </div>
    </div>

    <div v-if="!analytics && hasSearched && !loading && !error" class="no-data">
      <i class="fa-solid fa-inbox"></i>
      <p>No analytics returned for this range</p>
      <p class="no-data-subtitle">Check dates or SCMS availability</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const analyticsCards = [
  { key: 'total_visit', label: 'Total visits' },
  { key: 'total_patient', label: 'Total patients (distinct)' },
  { key: 'matched_selangkah_patient', label: 'Total Selangkah User as Patient' },
  { key: 'new_selangkah_user', label: 'New Selangkah users' },
  { key: 'existing_selangkah_user', label: 'Existing Selangkah users' },
  { key: 'selangkah_user_visit_gt_1', label: 'Selangkah users, visits > 1' },
  { key: 'non_selangkah_user', label: 'Non–Selangkah users' }
]

const startDate = ref('')
const endDate = ref('')
const analytics = ref(null)
const loading = ref(false)
const error = ref('')
const hasSearched = ref(false)
const today = ref('')

const formatMetric = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  return typeof v === 'number' ? v.toLocaleString() : String(v)
}

const toYmdLocal = (d) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Initialize dates
onMounted(() => {
  const now = new Date()
  today.value = toYmdLocal(now)
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  startDate.value = `${y}-${m}-01`
  endDate.value = today.value
  fetchSCMSData()
})

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Fetch SCMS data
const fetchSCMSData = async () => {
  if (!startDate.value || !endDate.value) {
    error.value = 'Please select both start and end dates'
    return
  }

  loading.value = true
  error.value = ''
  hasSearched.value = true
  analytics.value = null

  try {
    const response = await api.get('/api/finance/scms-tracker', {
      params: {
        start_date: startDate.value,
        end_date: endDate.value
      }
    })

    if (response.data.success && response.data.analytics) {
      analytics.value = response.data.analytics
      error.value = ''
    } else {
      error.value = 'Unexpected response from server'
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to fetch SCMS data'
    analytics.value = null
  } finally {
    loading.value = false
  }
}

</script>

<style scoped>
.scms-tracker {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
  width: 100%;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  margin-bottom: 16px;
}

.header-content h1 {
  font-size: clamp(1.5rem, 4vw, 1.875rem);
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
  margin-bottom: 8px;
}

.header-content p {
  font-size: clamp(0.75rem, 3vw, 0.875rem);
  color: #64748b;
}

.analytics-panel {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.analytics-panel-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.analytics-range {
  margin: 0 0 16px;
  font-size: 0.875rem;
  color: #6b7280;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.analytics-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.analytics-card-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  line-height: 1.3;
}

.analytics-card-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.filter-section {
  margin-bottom: 30px;
}

.filter-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.filter-card h3 {
  color: #374151;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 20px;
}

.view-mode-toggle {
  margin-bottom: 20px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.toggle-label {
  display: block;
  color: #374151;
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 12px;
}

.toggle-buttons {
  display: flex;
  gap: 8px;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.toggle-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.toggle-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.toggle-btn.active:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.month-selector {
  margin-bottom: 20px;
  padding: 16px;
  background: #f0f9ff;
  border-radius: 8px;
  border: 1px solid #0ea5e9;
}

.month-label {
  display: block;
  color: #0369a1;
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 12px;
}

.month-input-group {
  display: flex;
  gap: 12px;
  align-items: end;
  flex-wrap: wrap;
}

.month-input {
  padding: 10px 12px;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
  transition: all 0.2s ease;
  min-width: 150px;
}

.month-input:focus {
  outline: none;
  border-color: #0284c7;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.apply-month-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.apply-month-btn:hover:not(:disabled) {
  background: #0284c7;
  transform: translateY(-1px);
}

.apply-month-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  color: #0369a1;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 16px;
}

.date-inputs {
  display: flex;
  gap: 20px;
  align-items: end;
  flex-wrap: wrap;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-input-group label {
  color: #374151;
  font-weight: 500;
  font-size: 0.875rem;
}

.date-input {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
  transition: all 0.2s ease;
}

.date-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.fetch-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: fit-content;
}

.fetch-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.fetch-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.loading-section,
.error-section {
  margin: 20px 0;
}

.loading-card,
.error-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.loading-card {
  background: #e3f2fd;
  border-color: #2196f3;
}

.error-card {
  background: #ffebee;
  border-color: #f44336;
  color: #d32f2f;
}

.no-data {
  padding: 40px;
  text-align: center;
  color: #6b7280;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin: 20px 0;
}

.no-data i {
  font-size: 3rem;
  margin-bottom: 16px;
  color: #d1d5db;
}

.no-data p {
  font-size: 1rem;
  margin: 0;
}

.no-data-subtitle {
  font-size: 0.875rem !important;
  color: #9ca3af !important;
  margin-top: 8px !important;
}

@media (max-width: 1024px) {
  .scms-tracker {
    padding: 16px;
  }
}

@media (max-width: 768px) {
  .scms-tracker {
    padding: 12px;
  }
  
  .date-inputs {
    flex-direction: column;
    gap: 16px;
  }

  .date-input-group {
    width: 100%;
  }

  .fetch-btn {
    width: 100%;
    justify-content: center;
  }

  .toggle-buttons {
    flex-direction: column;
  }

  .toggle-btn {
    width: 100%;
    justify-content: center;
  }

  .month-input-group {
    flex-direction: column;
    align-items: stretch;
  }

  .month-input {
    width: 100%;
  }

  .apply-month-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .scms-tracker {
    padding: 8px;
  }
}
</style>
